/**
 * CardStage — character (left) and scene (right) card clusters around Muse.
 *
 * Replaces the old fan layout (KEYSTONE-1256). Mirrors the prototype's two
 * side-by-side flex sections (`.card-sections`): each cluster lives in its own
 * half-width section and is packed/centered within it (originX = sectionW/2)
 * using the count-based scaling + staggered offset grid (see cardLayout.ts).
 * The Muse orb is centered over the boundary between the two sections; each
 * cluster leaves clearance on its Muse-facing edge.
 *
 * Cards spring to their slots (useCardAnimation, native-driven) so the cluster
 * reflows when cards are added/removed; a newly seen card grows in from the
 * section center (spawnX = 0) at scale 0.
 *
 * When a card in a cluster is focused, that cluster switches to the prototype's
 * "Bunch" mode (see bunchFocus in cardLayout): the focused card scales up at the
 * cluster center and the rest scatter into a pile behind it.
 */

import React, { useEffect, useMemo, useState } from "react";
import { View, Text, Animated, LayoutChangeEvent } from "react-native";
import { colors, textStyle, useScale, design, borderRadius } from "../../styles";
import EntityCard from "./EntityCard";
import { layoutCluster, clusterCenterX, bunchFocus, CARD_ASPECT } from "./cardLayout";
import { useCardAnimation, CardTarget, targetsKey } from "./useCardAnimation";

/** Base card width of ImageItemCard — the layout transform scale is relative to this. */
const CARD_BASE_W = design.entityCard.width;

/**
 * "Characters" / "Places" pill header above each cluster (Figma 4392:29450).
 * When `hidden`, the pill is invisible but its layout box is preserved so the
 * card band below stays at the same vertical offset.
 */
function SectionHeader({ title, hidden }: { title: string; hidden?: boolean }) {
  return (
    <View style={{ alignItems: "center", paddingTop: 0, paddingBottom: 4, opacity: hidden ? 0 : 1 }}>
      <View
        style={{
          backgroundColor: colors.bg,
          borderRadius: borderRadius.round,
          paddingVertical: 2,
          paddingHorizontal: 20,
        }}
      >
        <Text
          style={{
            ...textStyle.heading,
            fontSize: design.sectionLabel.fontSize,
            lineHeight: design.sectionLabel.fontSize,
            color: colors.text,
            textAlign: "center",
            includeFontPadding: false,
            textAlignVertical: "center",
          }}
        >
          {title}
        </Text>
      </View>
    </View>
  );
}
const CARD_BASE_H = CARD_BASE_W * CARD_ASPECT;

export type StageEntity = {
  type: "character" | "scene";
  entityId: string;
  name: string;
  visualDescription: string;
  imageBase64?: string;
  isLoading?: boolean;
  hasError?: boolean;
  isModerated?: boolean;
};

interface CardStageProps {
  characters: StageEntity[];
  scenes: StageEntity[];
  focusedCharacterId: string | null;
  focusedSceneId: string | null;
  /** Centered Muse orb. */
  orb: React.ReactNode;
  /** Muse orb width (px) — used for the card-band clearance. */
  orbWidth: number;
}

interface Size {
  w: number;
  h: number;
}

export default function CardStage({
  characters,
  scenes,
  focusedCharacterId,
  focusedSceneId,
  orb,
  orbWidth,
}: CardStageProps) {
  // Each cluster section measures its own box (flex:1, fills the stage). Packing
  // uses the real available height so rows aren't starved — the prototype's
  // .stage is flex:1 and packs against clientHeight (clamped to 560).
  const [charBox, setCharBox] = useState<Size>({ w: 0, h: 0 });
  const [sceneBox, setSceneBox] = useState<Size>({ w: 0, h: 0 });
  const { sync, getStyle } = useCardAnimation();
  // ImageItemCard renders its dims through the app's responsive s() = uiScale*v,
  // so a card's real footprint is uiScale*CARD_BASE_W/H. We size the card View to
  // that footprint (so the card fills its box) and divide it back out of the
  // transform scale so the on-screen card width equals the layout's cardW.
  const { scale: uiScale } = useScale();
  const cardBoxW = uiScale * CARD_BASE_W;
  const cardBoxH = uiScale * CARD_BASE_H;

  const onLayout = (current: Size, set: (s: Size) => void) => (e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    if (width !== current.w || height !== current.h) set({ w: width, h: height });
  };

  // Build targets for one section: pack within its own box, positioned relative
  // to the section's center (originX = sectionW/2). Uses the measured section
  // height so rows aren't starved. Returns {targets, zById}.
  const buildSection = (
    entities: StageEntity[],
    box: Size,
    side: "left" | "right",
    focusedId: string | null,
  ): { targets: CardTarget[]; z: Record<string, number> } => {
    const targets: CardTarget[] = [];
    const z: Record<string, number> = {};
    if (box.w <= 0 || box.h <= 0) return { targets, z };
    const originX = box.w / 2;
    const layout = layoutCluster(side, originX, box.h, orbWidth, entities.length);
    // Visual width = (transform scale) * cardBoxW = scale * uiScale * CARD_BASE_W.
    // We want that to equal the layout's cardW, so divide uiScale back out.
    const scale = layout.cardW / (uiScale * CARD_BASE_W);
    const focusActive = !!focusedId && entities.some((e) => e.entityId === focusedId);
    const centerX = clusterCenterX(side, originX, orbWidth, layout.cardW);
    let pileIndex = 0;
    entities.forEach((entity, i) => {
      const slot = layout.slots[i];
      if (!slot) return;
      if (focusActive) {
        const isSel = entity.entityId === focusedId;
        const f = bunchFocus(entity.entityId, isSel, isSel ? 0 : pileIndex++);
        targets.push({ id: entity.entityId, x: centerX + f.x, y: f.y, rotation: f.rotation, scale: f.scale, spawnX: 0 });
        z[entity.entityId] = f.z;
      } else {
        targets.push({ id: entity.entityId, x: slot.x, y: slot.y, rotation: slot.rotation, scale, spawnX: 0 });
        z[entity.entityId] = slot.z;
      }
    });
    return { targets, z };
  };

  const charSection = useMemo(
    () => buildSection(characters, charBox, "left", focusedCharacterId),
    [characters, charBox, orbWidth, focusedCharacterId],
  );
  const sceneSection = useMemo(
    () => buildSection(scenes, sceneBox, "right", focusedSceneId),
    [scenes, sceneBox, orbWidth, focusedSceneId],
  );

  // Drive springs across all cards whenever any target changes.
  const allTargets = useMemo(
    () => [...charSection.targets, ...sceneSection.targets],
    [charSection, sceneSection],
  );
  const key = targetsKey(allTargets);
  useEffect(() => {
    // Always sync (even when empty) so cleared cards' springs are stopped and
    // their animated values pruned.
    sync(allTargets);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderCard = (
    entity: StageEntity,
    box: Size,
    z: Record<string, number>,
    focusedId: string | null,
  ) => {
    const style = getStyle(entity.entityId);
    if (!style) return null; // not yet synced (first frame) — appears next render
    const isFocused = entity.entityId === focusedId;
    return (
      <Animated.View
        key={entity.entityId}
        style={[
          {
            position: "absolute",
            // Center the base-size card box on the section center; the animated
            // transform (translate + scale, scaled around the View center)
            // places and sizes it.
            left: box.w / 2 - cardBoxW / 2,
            top: box.h / 2 - cardBoxH / 2,
            width: cardBoxW,
            height: cardBoxH,
            alignItems: "center",
            justifyContent: "center",
            zIndex: isFocused ? 100000 : z[entity.entityId] ?? 1000,
          },
          style,
        ]}
      >
        <EntityCard entity={entity} isFocused={isFocused} />
      </Animated.View>
    );
  };

  return (
    <View style={{ flex: 1, flexDirection: "row", position: "relative" }}>
      {/* Each section: a "Characters"/"Places" header above a flex:1 card area.
          The card area is what gets measured, so the packing height already
          excludes the header — no manual offset needed. */}
      <View style={{ flex: 1 }}>
        {/* Title shows only once the cluster has a card; an invisible spacer keeps
            the card band's top edge aligned with the other section's. */}
        <SectionHeader title="Characters" hidden={characters.length === 0} />
        <View style={{ flex: 1, position: "relative" }} onLayout={onLayout(charBox, setCharBox)}>
          {characters.map((e) => renderCard(e, charBox, charSection.z, focusedCharacterId))}
        </View>
      </View>
      <View style={{ flex: 1 }}>
        <SectionHeader title="Places" hidden={scenes.length === 0} />
        <View style={{ flex: 1, position: "relative" }} onLayout={onLayout(sceneBox, setSceneBox)}>
          {scenes.map((e) => renderCard(e, sceneBox, sceneSection.z, focusedSceneId))}
        </View>
      </View>
      {/* Muse, centered over the card-area band (below the headers) so it lines
          up with the cards. A hidden header spacer matches the header height. */}
      <View
        style={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0, zIndex: 1 }}
        pointerEvents="box-none"
      >
        <View style={{ opacity: 0 }} pointerEvents="none">
          <SectionHeader title="" />
        </View>
        <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }} pointerEvents="box-none">
          {orb}
        </View>
      </View>
    </View>
  );
}
